/**
 * Utility for processing SVG maps with dynamic location data
 */
import {getBasePath} from "@/shared/utils/env";

export type LocationMapData = {
    id: string;
    href: string;
    name: string;
    abbreviation?: string; // State abbreviation (e.g., "HI", "FL")
};

/**
 * Replaces location data in SVG string
 * @param svgString - Raw SVG string
 * @param locations - Array of location data to replace
 * @returns Processed SVG string with replaced data
 */
export function processSvgMap(svgString: string | unknown, locations: Array<LocationMapData>): string {
    // Ensure svgString is a string
    if (typeof svgString !== "string") {
        console.error("processSvgMap: svgString must be a string", typeof svgString);
        return "";
    }

    let processedSvg = svgString;

    // Create maps for quick lookup
    const locationMapById = new Map<string, LocationMapData>();
    const locationMapByHref = new Map<string, LocationMapData>();
    const allowedLocationIds = new Set<string>();

    locations.forEach((loc) => {
        locationMapById.set(loc.id.toLowerCase(), loc);
        allowedLocationIds.add(loc.id.toLowerCase());

        // Map by new href format: /rentals/california -> california
        const newHrefParts = loc.href.match(/\/rentals\/([^/]+)/);
        if (newHrefParts) {
            const locationId = newHrefParts[1].toLowerCase();
            locationMapByHref.set(locationId, loc);
            allowedLocationIds.add(locationId);
        }

        // Also map by old href pattern for matching SVG content
        // Pattern: /usa/Hawaii/ or /vacation-rentals/Mexico/
        const oldHrefParts = loc.href.match(/\/([^/]+)\/([^/]+)\//);
        if (oldHrefParts) {
            const locationId = oldHrefParts[2].toLowerCase();
            locationMapByHref.set(locationId, loc);
            allowedLocationIds.add(locationId);
        }
    });

    // Process each <a> block that contains location data
    // Pattern: <a xlink:href="/path/to/LocationName/">...</a>
    processedSvg = processedSvg.replace(/<a\s+xlink:href="([^"]+)"[^>]*>([\s\S]*?)<\/a>/g, (match, href, content) => {
        // Extract location identifier from href
        // Examples: /usa/Hawaii/ -> hawaii, /vacation-rentals/Mexico/ -> mexico
        const matchResult = href.match(/\/([^/]+)\/([^/]+)\//);
        if (matchResult) {
            const locationId = matchResult[2].toLowerCase();
            const location = locationMapByHref.get(locationId) || locationMapById.get(locationId);

            // Only process if location is in the allowed list
            if (location && allowedLocationIds.has(locationId)) {
                // Replace href
                const newHref = `xlink:href="${getBasePath()}${location.href}"`;
                let newMatch = match.replace(/xlink:href="[^"]+"/, newHref);

                // Add title attribute for hover tooltip with full name
                if (!newMatch.includes("title=")) {
                    newMatch = newMatch.replace(/<a\s+([^>]*)>/, `<a $1 title="${location.name}">`);
                }

                // Add data-full-name attribute for CSS hover
                if (!newMatch.includes("data-full-name=")) {
                    newMatch = newMatch.replace(/<a\s+([^>]*)>/, `<a $1 data-full-name="${location.name}">`);
                }

                // Replace text content inside this <a> block
                // Keep abbreviation if provided, otherwise use name
                const displayText = location.abbreviation || location.name;
                const processedContent = content.replace(/<text[^>]*>([\s\S]*?)<\/text>/g, (textMatch: string, textContent: string) => {
                    const trimmedText = textContent.trim();
                    // Replace with abbreviation or name
                    return textMatch.replace(trimmedText, displayText);
                });

                return newMatch.replace(content, processedContent);
            } else {
                // Hide elements that are not in the allowed list
                return "";
            }
        }
        return match;
    });

    // Also handle standalone xlink:href replacements (for cases outside <a> blocks)
    processedSvg = processedSvg.replace(/xlink:href="([^"]+)"/g, (match, href) => {
        const matchResult = href.match(/\/([^/]+)\/([^/]+)\//);
        if (matchResult) {
            const locationId = matchResult[2].toLowerCase();
            const location = locationMapByHref.get(locationId) || locationMapById.get(locationId);
            if (location && allowedLocationIds.has(locationId)) {
                return `xlink:href="${location.href}"`;
            }
        }
        return match;
    });

    // Hide standalone <path> and <text> elements that are not inside <a> tags
    // if they don't match any location in the array
    processedSvg = processedSvg.replace(/<path[^>]*>[\s\S]*?<\/path>/g, (match) => {
        // Check if this path is already inside an <a> tag (processed above)
        // If not, we might need to hide it, but this is complex
        // For now, keep all paths
        return match;
    });

    return processedSvg;
}
